import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {SearchInput} from "@/components/search-input";
import {useQuery} from "react-query";
import {useDebounce} from "use-debounce";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";
import {useDepartmentApi} from "@/api/platform/useDepartments";

type DepartmentFilterProps = ColumnFilterProps & {
    clientID?: string;
}

export const DepartmentFilter: React.FC<DepartmentFilterProps> = ({label, onChange, filterParams, clientID}) => {
    const deptApi = useDepartmentApi();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 300);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const getInitialDepartment = useCallback((): { id: string; name: string } | null => {
        if (filterParams.departmentID && filterParams.departmentName) {
            return {
                id: filterParams.departmentID as string,
                name: filterParams.departmentName as string,
            };
        }
        return null;
    }, [filterParams.departmentID, filterParams.departmentName]);

    const [appliedDepartment, setAppliedDepartment] = useState<{ id: string; name: string } | null>(getInitialDepartment);
    const [selectedDepartment, setSelectedDepartment] = useState<{ id: string; name: string } | null>(getInitialDepartment);

    const {data: departments = [], isLoading: loading} = useQuery(
        ["departments-filter", debouncedSearch, clientID],
        () => deptApi.list({ clientID: clientID ?? "", search: debouncedSearch, limit: 50, archived: false })
            .then(([res]) => res?.items ?? []),
        {
            enabled: isOpen,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) setSelectedDepartment(appliedDepartment);
        setIsOpen(!isOpen);
    }, [isOpen, appliedDepartment]);

    const handleSelect = useCallback((dept: { id: string; name: string } | null, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedDepartment(dept);
    }, []);

    const handleApply = useCallback(() => {
        setAppliedDepartment(selectedDepartment);
        if (selectedDepartment) {
            onChange({departmentID: selectedDepartment.id, departmentName: selectedDepartment.name});
        } else {
            onChange({departmentID: undefined, departmentName: undefined});
        }
        setIsOpen(false);
        setSearch("");
    }, [selectedDepartment, onChange]);

    const handleClear = useCallback(() => {
        setSelectedDepartment(null);
        setAppliedDepartment(null);
        onChange({departmentID: undefined, departmentName: undefined});
    }, [onChange]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 0);
        }
    }, [isOpen]);

    useEffect(() => {
        const newDept = filterParams.departmentID && filterParams.departmentName
            ? {id: filterParams.departmentID as string, name: filterParams.departmentName as string}
            : null;
        setAppliedDepartment(newDept);
        setSelectedDepartment(newDept);
    }, [filterParams.departmentID, filterParams.departmentName]);

    // Reset when client changes
    const prevClientID = useRef(clientID);
    useEffect(() => {
        if (prevClientID.current !== clientID) {
            prevClientID.current = clientID;
            setAppliedDepartment(null);
            setSelectedDepartment(null);
            onChange({departmentID: undefined, departmentName: undefined});
        }
    }, [clientID, onChange]);

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {appliedDepartment && (
                    <span className="text-xs text-blue-600 font-normal">
                        ({appliedDepartment.name})
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 w-[350px] flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    <div className="p-2">
                        <SearchInput ref={searchInputRef} initialValue={search} onChange={setSearch} />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        <button
                            onClick={(e) => handleSelect(null, e)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                        >
                            <input type="checkbox" checked={!selectedDepartment} onChange={() => {}} className="cursor-pointer w-4 h-4" />
                            <span>All</span>
                        </button>
                        {loading ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">Loading...</div>
                        ) : departments.length === 0 ? (
                            <div className="px-4 py-2 text-gray-500 text-center font-normal">No results found</div>
                        ) : (
                            departments.map((dept) => (
                                <button
                                    key={dept.id}
                                    onClick={(e) => handleSelect(dept, e)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                                >
                                    <input type="checkbox" checked={selectedDepartment?.id === dept.id} onChange={() => {}} className="cursor-pointer w-4 h-4" />
                                    <span>{dept.name}</span>
                                </button>
                            ))
                        )}
                    </div>
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
